import {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useInView } from 'react-intersection-observer'
import styled, { css } from 'styled-components'

import config from '@config'
import { desktopCSS, layout } from '@measures/responsive'
import { RelatedEntity } from '@utils/entities'
import { setLocalStorageItem } from '@utils/localStorage'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import useFollowMyTeam from '@hooks/useFollowMyTeam'
import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import useSportEntities from '@hooks/useSportEntities'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import Blink from '@components/Blink'
import BackgroundImage from '@components/EntityHeader/BackgroundImage'
import CollapsibleEntityHeader from '@components/EntityHeader/CollapsibleEntityHeader'
import FollowYourEntity from '@components/EntityHeader/FollowYourEntity'
import EntityImage from '@components/EntityHeader/Image'
import Subtitle from '@components/EntityHeader/Subtitle'
import Title from '@components/EntityHeader/Title'
import FmtTooltipTeamPage from '@components/FmtTooltip/FmtTooltipTeamPage'
import SportsPartner from '@components/SportsPartner'

import { TabsWrapper } from './LayoutTabs'

import type { Image } from '@utils/cook/types'

const {
  header: {
    mobileAndTablet: { offsetFirstRow: headerOffsetFirstRowMobileAndTablet },
  },
} = layout

const {
  fmtOnboarding: { localStorageHideFmtOnboardingTooltipKey },
} = config

export interface EntityHeaderAPIProps {
  entityId: string
  title: string
  isFollowingEnabled: boolean
  subtitle?: string
  sportradarId?: string
  backgroundImage: Image<'16_9' | '1_1'>
  image: Image<'free'>
  partner?: {
    name: string
    logo: Image<'free'>
    title: string
    url: string
  }
  relatedEntities?: RelatedEntity[]
}

const EntityHeaderWrapper = styled.div`
  ${({
    theme: {
      measures: { stretchWidthOnMobile },
      spacing: { spacing20 },
      colors: { staticBlack },
      utils: { opacity },
    },
  }) => css`
    position: relative;
    overflow: hidden;

    background: linear-gradient(
      180deg,
      ${opacity(staticBlack, 'a3')} 0%,
      ${opacity(staticBlack, '00')} 34.06%
    );

    margin-top: -${spacing20};

    ${desktopCSS(css`
      height: 183px;
      margin-top: 0;
    `)}

    ${stretchWidthOnMobile()};

    + ${TabsWrapper} {
      margin-top: -${spacing20};
    }
  `}
`

const StyledRelatedEntity = styled(Blink)`
  ${({
    theme: {
      colors: { staticWhite },
      typography: {
        bodycopy: {
          xsmall: { bundledCSS: xsmallBodycopyCSS },
          small2: { bundledCSS: small2BodycopyCSS },
        },
      },
      utils: { ellipsis },
    },
  }) => css`
    text-decoration: none;
    color: ${staticWhite};

    ${xsmallBodycopyCSS};
    text-align: center;

    ${ellipsis('100%', 1)};

    ${desktopCSS(css`
      ${small2BodycopyCSS};
      text-align: left;
    `)}
  `}
`

const ContentWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;

  padding-top: ${headerOffsetFirstRowMobileAndTablet}px;

  ${desktopCSS(css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-top: 0;
  `)}
`

const GridWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8, spacing24 },
    },
  }) => css`
    padding: ${spacing24};

    display: grid;
    grid-auto-rows: min-content;
    row-gap: ${spacing8};

    ${desktopCSS(css`
      padding: 0 ${spacing24};
      column-gap: ${spacing24};
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
    `)}
  `}
`

const ImageWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    gap: ${spacing12};
    justify-content: center;
    align-items: center;

    ${desktopCSS(css`
      grid-row: span 2;
    `)}
  `}
`

const TitleWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    display: grid;

    ${desktopCSS(css`
      grid-template-columns: auto auto minmax(0, 1fr);
      align-items: center;

      & > *:not(:first-child) {
        margin-left: ${spacing24};
      }
    `)}
  `}
`

const StyledSportsPartner = styled(SportsPartner)`
  ${({
    theme: {
      colors: { staticBlack },
      utils: { opacity },
    },
  }) => css`
    border: 0 none;
    background: ${opacity(staticBlack, '66')};
    backdrop-filter: blur(14px);

    ${desktopCSS(css`
      position: absolute;
      bottom: 0;
      right: 0;

      border-top-left-radius: 16px;
      border-top-width: 1px;
      width: auto;
      min-width: 300px;
      max-width: 836px;
    `)}
  `}
`

export const FollowYourEntityMobilePlaceholder = styled.div`
  width: 24px;

  ${desktopCSS(css`
    display: none;
  `)}
`

const FtmButtonWrapper = styled.div`
  position: relative;
`

const EntityHeader: FunctionComponent<EntityHeaderAPIProps> = (props) => {
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()

  const pageMetadata = usePageMetadata()
  const {
    entityId,
    title,
    subtitle,
    backgroundImage,
    image,
    partner,
    isFollowingEnabled,
  } = props

  const relatedEntities = (pageMetadata as PageMetadata)?.relatedEntities
  const mainRelatedEntity = relatedEntities?.[0]
  const { getSportEntityById } = useSportEntities()
  const entityHeaderRef = useRef<HTMLDivElement>(null)
  const fmtButtonRef = useRef<HTMLDivElement>(null)
  const sportEntity = useMemo(
    () => getSportEntityById(entityId),
    [entityId, getSportEntityById],
  )
  const useFollowMyTeamProps = useMemo(
    () => ({
      entityType: sportEntity?.entityType ?? 'default',
      enabled: isFollowingEnabled,
    }),
    [sportEntity, isFollowingEnabled],
  )

  const { isFollowing, toggleFollow } = useFollowMyTeam(useFollowMyTeamProps)

  const [
    shouldShowCollapsibleEntityHeader,
    setShouldShowCollapsibleEntityHeader,
  ] = useState(false)

  const onEntityHeaderInViewChangeHandler = useCallback(
    (inView: boolean) => {
      setShouldShowCollapsibleEntityHeader(!inView)
    },
    [setShouldShowCollapsibleEntityHeader],
  )

  const { ref } = useInView({
    rootMargin: '-188px 0px 0px 0px',
    onChange: onEntityHeaderInViewChangeHandler,
  })

  const onAddTeamClickEvent = useCallback<TrackingFnType<{ entityId: string }>>(
    ({ extraData }) => ({
      event: 'add_team',
      entity_id: extraData.entityId,
    }),
    [],
  )

  const trackedOnAddTeamClick = useTracking(onAddTeamClickEvent)

  const onToggleFollowing = useCallback(() => {
    if (sportEntity) {
      toggleFollow(sportEntity)
      setLocalStorageItem(localStorageHideFmtOnboardingTooltipKey, 'true')

      if (!isFollowing(sportEntity.entityId)) {
        trackedOnAddTeamClick({ entityId: sportEntity.entityId })
      }
    }
  }, [sportEntity, toggleFollow, trackedOnAddTeamClick, isFollowing])

  return (
    <>
      {isFollowMyTeamEnabled && isFollowingEnabled && (
        <FmtTooltipTeamPage
          fmtButtonRef={fmtButtonRef}
          entityHeaderRef={entityHeaderRef}
          title={title}
          isFollowing={isFollowing(entityId)}
        />
      )}
      <CollapsibleEntityHeader
        backgroundImage={backgroundImage}
        image={image}
        isFollowingEnabled={isFollowingEnabled}
        shouldShowCollapsibleEntityHeader={shouldShowCollapsibleEntityHeader}
        isFollowing={isFollowing(entityId)}
        onToggleFollowing={onToggleFollowing}
      />
      <EntityHeaderWrapper ref={ref}>
        <BackgroundImage image={backgroundImage}>
          <ContentWrapper ref={entityHeaderRef}>
            <GridWrapper>
              <ImageWrapper>
                {isFollowMyTeamEnabled && isFollowingEnabled && (
                  <FollowYourEntityMobilePlaceholder />
                )}
                <EntityImage image={image} />
                {isFollowMyTeamEnabled && isFollowingEnabled && (
                  <FollowYourEntity
                    variant="mobile"
                    isFollowing={isFollowing(entityId)}
                    onToggleFollowing={onToggleFollowing}
                  />
                )}
              </ImageWrapper>
              <TitleWrapper>
                <Title>{title}</Title>
                {isFollowMyTeamEnabled && isFollowingEnabled && (
                  <FtmButtonWrapper ref={fmtButtonRef}>
                    <FollowYourEntity
                      variant="desktop"
                      isFollowing={isFollowing(entityId)}
                      onToggleFollowing={onToggleFollowing}
                    />
                  </FtmButtonWrapper>
                )}
              </TitleWrapper>
              {mainRelatedEntity?.title ? (
                <StyledRelatedEntity {...mainRelatedEntity.link}>
                  {mainRelatedEntity.title}
                </StyledRelatedEntity>
              ) : (
                subtitle && <Subtitle>{subtitle}</Subtitle>
              )}
            </GridWrapper>
            <StyledSportsPartner partner={partner} />
          </ContentWrapper>
        </BackgroundImage>
      </EntityHeaderWrapper>
    </>
  )
}

const widget = {
  kind: ['entity-header'],
  component: EntityHeader,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
